// src/Components/Cadastro/ImportProducts/FormImportProducts.tsx
import React, { useState, useRef } from 'react';
import { FaUpload, FaCheckCircle, FaTimes, FaExclamationCircle, FaStore } from 'react-icons/fa';
import Layout from '../../data/Layout';
import api from '../../../Services/api';
import { useAppSelector } from '../../../redux/store';
import { notify, notifyError } from '../../notify/notify-component';
import { IFormImportProducts } from '../../../Pages/Cadastro/CadImportProducts';
import * as S from './styled';

interface UploadResponse {
  message?: string;
  imported_count?: number;
  errors?: string[];
}

export default function FormImportProducts({
  title,
  OnAddNewImport,
}: IFormImportProducts) {
  const storeMe = useAppSelector(({ storeMeReducer }) => storeMeReducer.value);
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<'success' | 'error' | null>(null);
  const [uploadResponse, setUploadResponse] = useState<UploadResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    if (file && (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                 file.type === 'application/vnd.ms-excel' || 
                 file.name.endsWith('.xlsx') || 
                 file.name.endsWith('.xls'))) {
      setSelectedFile(file);
      setUploadStatus(null);
      setUploadResponse(null);
    } else {
      notifyError('Por favor, selecione um arquivo Excel (.xlsx ou .xls)');
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      notifyError('Por favor, selecione um arquivo primeiro');
      return;
    }

    if (!storeMe?.storeActive?.id) {
      notifyError('Loja não identificada');
      return;
    }

    setIsUploading(true);
    setUploadStatus(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('store_id', storeMe.storeActive.id.toString());

    try {
      const response = await api.post('/api/v1/import-xd', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      const result: UploadResponse = response.data;
      setUploadStatus('success');
      setUploadResponse(result);
      notify(result.message || 'Produtos importados com sucesso!');
      
      setTimeout(() => {
        handleCancel();
      }, 3000);
      
    } catch (error: any) {
      setUploadStatus('error');
      const errorMessage = error.response?.data?.message || error.message || 'Erro desconhecido';
      setUploadResponse({ message: errorMessage });
      notifyError('Erro na importação: ' + errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    OnAddNewImport(false);
    setSelectedFile(null);
    setUploadStatus(null);
    setUploadResponse(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setUploadStatus(null);
    setUploadResponse(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Verificar se pode salvar (tem arquivo selecionado, não está fazendo upload e não tem erro)
  const canSave = selectedFile && !isUploading && uploadStatus !== 'success';
  
  // Debug - remover após testar
  console.log('Estado do botão:', {
    selectedFile: !!selectedFile,
    isUploading,
    uploadStatus,
    canSave,
    fileName: selectedFile?.name
  });

  return (
    <>
      <Layout
        title={title}
        isEditable={true} // Forçar true temporariamente para debug
        onClickSave={handleSubmit}
        onClickCancel={handleCancel}
        loading={isUploading}
      >
        {/* Informações da Loja */}
        <S.Wrapper>
          <div 
            className="containerFilter" 
            style={{ 
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              marginBottom: '20px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <FaStore style={{ fontSize: '24px', color: '#007bff' }} />
              <div>
                <h3 style={{ 
                  fontSize: '18px', 
                  fontWeight: '600', 
                  margin: '0 0 5px 0',
                  color: '#495057'
                }}>
                  {storeMe?.storeActive?.name || 'Loja Não Identificada'}
                </h3>
                <div style={{ fontSize: '14px', color: '#6c757d' }}>
                  ID: #{storeMe?.storeActive?.id || 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </S.Wrapper>

        {/* Área de Upload */}
        <S.Wrapper>
          <div className="containerTable" style={{ margin: '0' }}>
            <div className="rowControl">
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => !selectedFile && fileInputRef.current?.click()}
                style={{
                  border: `3px dashed ${isDragOver ? '#007bff' : selectedFile ? '#28a745' : '#dee2e6'}`,
                  borderRadius: '12px',
                  padding: '50px 20px',
                  textAlign: 'center',
                  cursor: selectedFile ? 'default' : 'pointer',
                  minHeight: '250px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  backgroundColor: isDragOver ? '#e7f1ff' : selectedFile ? '#d4edda' : 'transparent',
                  transition: 'all 0.3s ease'
                }}
              >
                {selectedFile ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                    <FaCheckCircle size={64} style={{ color: '#28a745' }} />
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px', color: '#28a745' }}>
                        Arquivo Selecionado
                      </div>
                      <div style={{ fontSize: '16px', marginBottom: '5px' }}>{selectedFile.name}</div>
                      <div style={{ fontSize: '14px', color: '#6c757d' }}>
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '15px' }}>
                      <button
                        type="button"
                        onClick={resetUpload}
                        className="btn btn-outline-danger"
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        disabled={isUploading}
                      >
                        <FaTimes />
                        Remover Arquivo
                      </button>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="btn btn-outline-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        disabled={isUploading}
                      >
                        <FaUpload />
                        Trocar Arquivo
                      </button>
                      {/* Botão de importação como fallback */}
                      <button
                        type="button"
                        onClick={handleSubmit}
                        className="btn btn-success btn-lg"
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '10px',
                          fontSize: '16px',
                          fontWeight: '600',
                          padding: '12px 24px'
                        }}
                        disabled={isUploading || !selectedFile}
                      >
                        {isUploading ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            Importando...
                          </>
                        ) : (
                          <>
                            <FaUpload />
                            Importar Produtos
                          </>
                        )}
                      </button>
                    </div>
                    {/* Indicação de que pode usar o botão Importar */}
                    <div style={{
                      backgroundColor: '#d1ecf1',
                      border: '1px solid #bee5eb',
                      borderRadius: '8px',
                      padding: '12px 20px',
                      fontSize: '14px',
                      color: '#0c5460',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FaCheckCircle style={{ color: '#28a745' }} />
                      Arquivo pronto! Use o botão "Importar Produtos" acima para continuar.
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                    <FaUpload size={64} style={{ color: '#6c757d' }} />
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '600', marginBottom: '10px' }}>
                        Solte seu arquivo aqui
                      </div>
                      <div style={{ fontSize: '16px', color: '#6c757d', marginBottom: '20px' }}>
                        ou clique para selecionar um arquivo Excel
                      </div>
                      <div style={{ fontSize: '14px', color: '#6c757d' }}>
                        Formatos aceitos: .xlsx, .xls (máximo 10MB)
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="btn btn-primary btn-lg"
                      style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                    >
                      <FaUpload />
                      Selecionar Arquivo
                    </button>
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        </S.Wrapper>

        {/* Status do Upload */}
        {uploadStatus && (
          <S.Wrapper>
            <div 
              className="containerFilter" 
              style={{ 
                margin: '0',
                backgroundColor: uploadStatus === 'success' ? '#d4edda' : '#f8d7da',
                border: `2px solid ${uploadStatus === 'success' ? '#c3e6cb' : '#f5c6cb'}`
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                {uploadStatus === 'success' ? (
                  <FaCheckCircle style={{ color: '#28a745', marginTop: '2px', fontSize: '24px' }} />
                ) : (
                  <FaExclamationCircle style={{ color: '#dc3545', marginTop: '2px', fontSize: '24px' }} />
                )}
                <div style={{ flex: 1 }}>
                  <h3 style={{ 
                    color: uploadStatus === 'success' ? '#155724' : '#721c24',
                    marginBottom: '10px',
                    fontSize: '20px',
                    fontWeight: '600'
                  }}>
                    {uploadStatus === 'success' ? 'Importação Concluída!' : 'Erro na Importação'}
                  </h3>
                  {uploadResponse && (
                    <div style={{ 
                      fontSize: '14px',
                      color: uploadStatus === 'success' ? '#155724' : '#721c24' 
                    }}>
                      {uploadResponse.message && <p style={{ marginBottom: '8px' }}>{uploadResponse.message}</p>}
                      {uploadResponse.imported_count && (
                        <p style={{ marginBottom: '8px', fontWeight: '600' }}>
                          ✅ Produtos importados: {uploadResponse.imported_count}
                        </p>
                      )}
                      {uploadResponse.errors && uploadResponse.errors.length > 0 && (
                        <div style={{ marginTop: '15px' }}>
                          <div style={{ fontWeight: '600', marginBottom: '8px' }}>❌ Erros encontrados:</div>
                          <ul style={{ paddingLeft: '20px', margin: '0' }}>
                            {uploadResponse.errors.map((error: string, index: number) => (
                              <li key={index} style={{ marginBottom: '5px' }}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  {uploadStatus === 'success' && (
                    <div style={{ 
                      marginTop: '15px', 
                      fontSize: '14px', 
                      color: '#155724',
                      fontStyle: 'italic'
                    }}>
                      Retornando à lista em alguns segundos...
                    </div>
                  )}
                </div>
              </div>
            </div>
          </S.Wrapper>
        )}
      </Layout>
    </>
  );
}